import { useEffect, useState, type RefObject } from 'react'

/** How long after the last scroll event the element counts as at rest. */
const AT_REST_MS = 500

/**
 * Whether the element behind `ref` is scrolling right now (0.46.0).
 *
 * The floating switcher lies on the chat; whoever reads upwards would read
 * under it. So it steps aside while the thread moves and returns as soon as
 * the thread has stood still for half a second -- like the bars of a mobile
 * browser. Only scroll events count: a tap, a new message arriving, a
 * keyboard opening do not move the thread by themselves.
 *
 * The listener hangs on the element itself, passive, and is removed with
 * the element; the timer is cleared with it, so a page left mid-scroll
 * does not flip state after unmount.
 */
export function useHiddenWhileScrolling(ref: RefObject<HTMLElement | null>): boolean {
  const [scrolling, setScrolling] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (element === null) return
    let timer: number | null = null
    const onScroll = () => {
      setScrolling(true)
      if (timer !== null) window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        timer = null
        setScrolling(false)
      }, AT_REST_MS)
    }
    element.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      element.removeEventListener('scroll', onScroll)
      if (timer !== null) window.clearTimeout(timer)
    }
  }, [ref])

  return scrolling
}
